import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ArrowRightIcon,
  GlobeAltIcon,
  BoltIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  SparklesIcon,
  LinkIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

import partner1 from '../../assets/img/partners/1.png';
import partner2 from '../../assets/img/partners/2.png';
import partner3 from '../../assets/img/partners/3.png';
import partner4 from '../../assets/img/partners/4.png';
import partner5 from '../../assets/img/partners/5.png';
import partner6 from '../../assets/img/partners/6.png';
import partner7 from '../../assets/img/partners/7.png';
import partner8 from '../../assets/img/partners/8.png';
import partner9 from '../../assets/img/partners/9.png';

function SectionLabel({ children }) {
  return (
    <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full hero-glass-tag text-[#2563EB]">
      {children}
    </span>
  );
}

const stats = [
  { value: '9+', label: 'Global Alliances' },
  { value: '4', label: 'Tier-1 Vendors' },
  { value: '24/7', label: 'Joint Support' },
  { value: '100%', label: 'Certified Delivery' },
];

const featuredPartners = [
  {
    id: 1,
    image: partner1,
    name: 'Fortinet',
    level: 'Gold Partner',
    category: 'Cybersecurity',
    accent: 'from-red-500/20 to-orange-500/10',
    ring: 'group-hover:shadow-red-500/20',
  },
  {
    id: 2,
    image: partner2,
    name: 'Juniper',
    level: 'Silver Partner',
    category: 'Networking',
    accent: 'from-cyan-500/20 to-blue-500/10',
    ring: 'group-hover:shadow-cyan-500/20',
  },
  {
    id: 3,
    image: partner3,
    name: 'Cisco',
    level: 'Certified Partner',
    category: 'Infrastructure',
    accent: 'from-blue-500/20 to-indigo-500/10',
    ring: 'group-hover:shadow-blue-500/20',
  },
  {
    id: 4,
    image: partner4,
    name: 'Huawei',
    level: 'Authorized Partner',
    category: 'Enterprise Tech',
    accent: 'from-rose-500/20 to-pink-500/10',
    ring: 'group-hover:shadow-rose-500/20',
  },
];

const alliancePartners = [
  { id: 5, image: partner5, alt: 'Technology Alliance Partner' },
  { id: 6, image: partner6, alt: 'Technology Alliance Partner' },
  { id: 7, image: partner7, alt: 'Technology Alliance Partner' },
  { id: 8, image: partner8, alt: 'Technology Alliance Partner' },
  { id: 9, image: partner9, alt: 'Technology Alliance Partner' },
];

const benefits = [
  {
    title: 'Global Network',
    description:
      'Tap into worldwide resources, certified expertise, and co-engineered solutions through our vetted partner ecosystem.',
    icon: GlobeAltIcon,
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    title: 'Innovation Hub',
    description:
      'Early access to emerging platforms—AI, zero-trust security, and cloud-native stacks—from industry-leading vendors.',
    icon: BoltIcon,
    gradient: 'from-violet-500 to-purple-500',
  },
  {
    title: 'Expert Support',
    description:
      'Joint delivery teams, vendor-backed SLAs, and dedicated architects ensure enterprise-grade outcomes at every stage.',
    icon: UserGroupIcon,
    gradient: 'from-emerald-500 to-teal-500',
  },
];

const pillars = [
  {
    title: 'Security & Compliance',
    description: 'Fortinet, Cisco, and certified frameworks for zero-trust and regulatory alignment.',
    icon: ShieldCheckIcon,
  },
  {
    title: 'Cloud & Infrastructure',
    description: 'Scalable networking, hybrid cloud, and resilient architectures from tier-1 providers.',
    icon: SparklesIcon,
  },
  {
    title: 'Co-Innovation',
    description: 'Pilot programs, proof-of-concepts, and go-to-market acceleration with vendor R&D teams.',
    icon: RocketLaunchIcon,
  },
];

const processSteps = [
  { step: '01', title: 'Discover', desc: 'Assess your stack, goals, and compliance requirements with our alliance architects.' },
  { step: '02', title: 'Align', desc: 'Match the right vendor programs, certifications, and licensing for your roadmap.' },
  { step: '03', title: 'Deploy', desc: 'Joint implementation with certified engineers and proven delivery playbooks.' },
  { step: '04', title: 'Scale', desc: 'Ongoing optimization, support, and expansion across your enterprise footprint.' },
];

export default function StrategicPartnerships() {
  const [refHero, inViewHero] = useInView({ threshold: 0.1, triggerOnce: true });
  const [refFeatured, inViewFeatured] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refMarquee, inViewMarquee] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refBenefits, inViewBenefits] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refProcess, inViewProcess] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refCta, inViewCta] = useInView({ threshold: 0.1, triggerOnce: true });
  const [hoveredPartner, setHoveredPartner] = useState(null);

  const marqueeLogos = [...featuredPartners, ...alliancePartners];

  return (
    <div className="bg-white dark:bg-gray-900 text-slate-900 dark:text-white overflow-x-hidden">
      {/* Hero */}
      <section
        ref={refHero}
        className="relative pt-28 pb-16 md:pt-32 md:pb-24 px-6 bg-gradient-to-b from-white via-slate-50/90 to-white dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 overflow-hidden"
      >
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#2563EB]/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={inViewHero ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <SectionLabel>Strategic Alliances</SectionLabel>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="text-slate-900 dark:text-white">Powering Innovation Through </span>
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Elite Partnerships
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            We unite with world-class technology leaders to deliver secure, scalable, and future-ready solutions—so your
            organization benefits from certified expertise at every layer of the stack.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }}
                animate={inViewHero ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.15 + i * 0.08 }}
                className="hero-glass-card rounded-2xl px-4 py-5 text-center"
              >
                <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#2563EB] to-cyan-500 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm text-slate-500 dark:text-gray-400 mt-1 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#partner-ecosystem"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all"
            >
              Explore Ecosystem <ArrowRightIcon className="w-5 h-5" />
            </a>
            <Link
              to="/partnership"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-gray-200 font-medium hover:border-[#2563EB]/40 hover:text-[#2563EB] transition-all hero-glass-tag"
            >
              Become a Partner
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Featured tier-1 partners */}
      <section id="partner-ecosystem" ref={refFeatured} className="py-20 md:py-28 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-white to-white dark:from-gray-800/50 dark:via-gray-900 dark:to-gray-900 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inViewFeatured ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-14"
          >
            <SectionLabel>Tier-1 Alliances</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Trusted <span className="text-[#2563EB]">Technology Leaders</span>
            </h2>
            <p className="text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
              Certified partnerships with global vendors powering enterprise security, networking, and infrastructure.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {featuredPartners.map((partner, i) => (
              <motion.article
                key={partner.id}
                initial={{ opacity: 0, y: 24 }}
                animate={inViewFeatured ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.1 }}
                onMouseEnter={() => setHoveredPartner(partner.id)}
                onMouseLeave={() => setHoveredPartner(null)}
                className={`group relative rounded-3xl overflow-hidden hero-glass-card transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${partner.ring}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${partner.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-slate-200/80 dark:ring-gray-700 group-hover:ring-[#2563EB]/30 transition-all" />
                <div className="relative p-6 md:p-8 flex flex-col items-center text-center min-h-[220px]">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20">
                    <CheckBadgeIcon className="w-3.5 h-3.5" />
                    {partner.level}
                  </span>
                  <img
                    src={partner.image}
                    alt={partner.name}
                    className="block max-h-16 w-auto object-contain mx-auto my-4 md:my-6 transition-transform duration-500 group-hover:scale-110 dark:brightness-0 dark:invert"
                  />
                  <h3 className="font-bold text-slate-900 dark:text-white text-lg">{partner.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-1 font-medium uppercase tracking-wide">
                    {partner.category}
                  </p>
                  {hoveredPartner === partner.id && (
                    <motion.div
                      layoutId="partner-glow"
                      className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 bg-[#2563EB]/20 blur-2xl rounded-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    />
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Infinite partner marquee */}
      <section ref={refMarquee} className="py-16 md:py-20 px-6 overflow-hidden border-y border-slate-100 dark:border-gray-800">
        <motion.div
          initial={{ opacity: 0 }}
          animate={inViewMarquee ? { opacity: 1 } : {}}
          className="max-w-7xl mx-auto"
        >
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-gray-500 mb-10">
            Extended Technology Alliance Network
          </p>
          <div className="clients-marquee-wrapper py-4">
            <div className="clients-marquee-track gap-8">
              {[...marqueeLogos, ...marqueeLogos].map((partner, index) => (
                <div
                  key={`marquee-${partner.id}-${index}`}
                  className="clients-logo-card flex-shrink-0 flex items-center justify-center h-24 px-10 rounded-2xl hero-glass-tag hover:hero-glow transition-all duration-300"
                >
                  <img
                    src={partner.image}
                    alt={'alt' in partner ? partner.alt : partner.name}
                    className="max-h-12 w-auto object-contain dark:brightness-0 dark:invert opacity-80 hover:opacity-100 transition-opacity"
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Ecosystem pillars */}
      <section className="py-20 md:py-28 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <SectionLabel>Alliance Framework</SectionLabel>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-5">
                Built for <span className="text-[#2563EB]">Enterprise Scale</span>
              </h2>
              <p className="text-slate-600 dark:text-gray-300 leading-relaxed mb-8">
                Our partnership model connects your business to certified vendors, co-delivered implementations, and
                continuous innovation—without compromising security or performance.
              </p>
              <div className="flex items-center gap-3 text-sm font-semibold text-[#2563EB]">
                <LinkIcon className="w-5 h-5" />
                <span>Vendor-certified · Jointly delivered · Fully supported</span>
              </div>
            </motion.div>

            <div className="lg:col-span-7 space-y-4">
              {pillars.map((pillar, i) => {
                const Icon = pillar.icon;
                return (
                  <motion.div
                    key={pillar.title}
                    initial={{ opacity: 0, x: 24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ x: 8 }}
                    className="group flex gap-5 p-5 md:p-6 rounded-2xl hero-glass-card hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB]/15 to-cyan-500/10 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white mb-1">{pillar.title}</h3>
                      <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{pillar.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section ref={refBenefits} className="py-20 md:py-28 px-6 bg-gradient-to-b from-slate-50/90 to-white dark:from-gray-800/50 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inViewBenefits ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-14"
          >
            <SectionLabel>Why It Matters</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Partnership <span className="text-[#2563EB]">Benefits</span>
            </h2>
            <p className="text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
              Unique advantages for clients who leverage our strategic vendor relationships.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 24 }}
                  animate={inViewBenefits ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="group relative rounded-3xl p-8 hero-glass-card overflow-hidden"
                >
                  <div
                    className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${benefit.gradient} opacity-10 group-hover:opacity-20 blur-2xl transition-opacity`}
                  />
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-6 shadow-lg`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{benefit.title}</h3>
                  <p className="text-slate-600 dark:text-gray-300 text-sm leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section ref={refProcess} className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inViewProcess ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-14"
          >
            <SectionLabel>How We Partner</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              From <span className="text-[#2563EB]">Discovery</span> to Scale
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            {processSteps.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={inViewProcess ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.1 }}
                className="relative hero-glass-card rounded-2xl p-6 text-center group hover:-translate-y-1 transition-transform"
              >
                <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB] to-cyan-500 text-white text-sm font-bold mb-4 shadow-lg shadow-blue-500/25">
                  {item.step}
                </span>
                <h3 className="font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        ref={refCta}
        className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inViewCta ? { opacity: 1, y: 0 } : {}}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Join Our Alliance Network?</h2>
          <p className="text-xl text-white/90 mb-10">
            Whether you need vendor-backed solutions or want to explore a technology partnership—we&apos;re here to connect.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-blue-600 font-medium rounded-lg shadow-lg hover:bg-gray-100 transition"
            >
              Talk to Our Team
            </Link>
            <Link
              to="/partnership"
              className="px-8 py-4 border-2 border-white text-white font-medium rounded-lg hover:bg-white/10 transition"
            >
              Become a Partner
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
